import React, { useState, useEffect } from 'react';
import { Layout, PlusCircle, Trash2, Save, Search, Edit, X } from 'lucide-react';
import { api } from '../services/apiClient';

const FormatManager = () => {
  const [formats, setFormats] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [newFormat, setNewFormat] = useState({ code: '', name: '' });
  const [editingId, setEditingId] = useState(null);
  const [editFormat, setEditFormat] = useState({ code: '', name: '' });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchFormats();
  }, []);

  async function fetchFormats() {
    try {
      const data = await api.formats.list();
      setFormats(data);
    } catch (err) {
      console.error('Errore caricamento formati:', err);
    }
  }

  async function handleAddFormat(e) {
    e.preventDefault();
    if (!newFormat.code || !newFormat.name) return;

    try {
      const created = await api.formats.create({ code: parseInt(newFormat.code), name: newFormat.name });
      setFormats([...formats, created].sort((a, b) => a.code - b.code));
      setIsAdding(false);
      setNewFormat({ code: '', name: '' });
    } catch (err) {
      alert(`Errore: ${err.message}`);
    }
  }

  async function handleUpdateFormat(id) {
    if (!editFormat.code || !editFormat.name) return;

    try {
      await api.formats.update(id, { code: parseInt(editFormat.code), name: editFormat.name });
      setFormats(formats.map(f => f.id === id ? { ...f, code: parseInt(editFormat.code), name: editFormat.name } : f).sort((a, b) => a.code - b.code));
      setEditingId(null);
      setEditFormat({ code: '', name: '' });
    } catch (err) {
      alert(`Errore: ${err.message}`);
    }
  }

  async function handleDeleteFormat(id) {
    if (!window.confirm('Sei sicuro di voler eliminare questo formato? Le illustrazioni associate perderanno il formato.')) return;

    try {
      await api.formats.delete(id);
      setFormats(formats.filter(f => f.id !== id));
    } catch (err) {
      alert(`Errore: ${err.message}`);
    }
  }

  const filteredFormats = formats.filter(f =>
    f.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    f.code.toString().includes(searchTerm)
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2 text-brand-dark">
          <Layout size={24} />
          <h3 className="text-xl font-semibold">Gestione Formati</h3>
        </div>
        {!isAdding && (
          <button
            onClick={() => setIsAdding(true)}
            className="flex items-center gap-2 bg-brand-dark text-white px-4 py-2 rounded-lg hover:bg-opacity-90 transition-all"
          >
            <PlusCircle size={18} /> Aggiungi Formato
          </button>
        )}
      </div>

      {isAdding && (
        <div className="bg-white p-6 rounded-xl border border-brand-main shadow-sm max-w-md mx-auto">
          <form onSubmit={handleAddFormat} className="space-y-3">
            <div>
              <input
                type="number"
                className="w-full p-2 border border-gray-200 rounded-md focus:ring-2 focus:ring-brand-main outline-none"
                placeholder="Codice (es. 1, 2, 3...)"
                value={newFormat.code}
                onChange={e => setNewFormat({ ...newFormat, code: e.target.value })}
                required
              />
            </div>
            <div>
              <input
                className="w-full p-2 border border-gray-200 rounded-md focus:ring-2 focus:ring-brand-main outline-none"
                placeholder="Nome formato (es. Stampe A4, Quadrati...)"
                value={newFormat.name}
                onChange={e => setNewFormat({ ...newFormat, name: e.target.value })}
                required
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button type="submit" className="bg-brand-dark text-white px-4 py-2 rounded-lg hover:bg-opacity-90">
                Salva
              </button>
              <button type="button" onClick={() => { setIsAdding(false); setNewFormat({ code: '', name: '' }); }} className="px-4 py-2 text-gray-500">
                Annulla
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="flex justify-center mb-6">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Cerca formato..."
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-brand-main"
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {filteredFormats.length === 0 ? (
          <div className="col-span-full text-center py-10 text-gray-400 italic">
            Nessun formato trovato.
          </div>
        ) : (
          filteredFormats.map(fmt => (
            <div key={fmt.id} className="bg-white p-4 rounded-xl border border-brand-main shadow-sm flex justify-between items-center group">
              {editingId === fmt.id ? (
                <div className="flex flex-1 gap-2">
                  <input
                    type="number"
                    className="w-12 p-1 border rounded outline-none focus:ring-1 focus:ring-brand-main"
                    value={editFormat.code}
                    onChange={e => setEditFormat({ ...editFormat, code: e.target.value })}
                    required
                  />
                  <input
                    className="flex-1 p-1 border rounded outline-none focus:ring-1 focus:ring-brand-main"
                    value={editFormat.name}
                    onChange={e => setEditFormat({ ...editFormat, name: e.target.value })}
                    required
                    autoFocus
                  />
                  <button onClick={() => handleUpdateFormat(fmt.id)} className="text-green-600 p-1"><Save size={18}/></button>
                  <button onClick={() => setEditingId(null)} className="text-gray-400 p-1"><X size={18}/></button>
                </div>
              ) : (
                <>
                  <span className="font-medium text-brand-dark">{fmt.code} — {fmt.name}</span>
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => { setEditingId(fmt.id); setEditFormat({ code: fmt.code, name: fmt.name }); }}
                      className="text-blue-500 hover:text-blue-700 p-1"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteFormat(fmt.id)}
                      className="text-red-400 hover:text-red-600 p-1"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default FormatManager;
